import React from 'react';
import {
  Avatar,
  Box,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { HtmlRenderer } from './HtmlRenderer';
import { AppColors, typographyClamps } from '../utils/utils';

interface Feature {
  title: string;
  description: string;
}

interface SectionViewProps {
  idx: number;
  label: string;
  title?: string;
  subtitle?: string;
  features: Feature[];
  footerText: string;
  imagePath: string;
  imageBGPath?: string;
  imageAlign?: 'start' | 'center' | 'end';
  isForFeature?: boolean;
  titleTextColor?: string;
  transparentBackground?: boolean;
}

export const HowItWorksSectionView: React.FC<SectionViewProps> = ({
  idx,
  label,
  title,
  subtitle,
  features,
  footerText,
  imagePath,
  imageBGPath,
  imageAlign,
  isForFeature = true,
  titleTextColor,
  transparentBackground,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // e.g. <600px

  const isImageOnRight = idx % 2 !== 0;

  const gridSize = isForFeature
    ? { xs: 12, sm: 5.5, md: 5.5 }
    : { xs: 5.5, sm: 5.5, md: 5.5 };

  const getScreenshotView = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: imageAlign ?? 'center',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {imageBGPath && (
        <Box
          component='img'
          src={imageBGPath}
          sx={{
            position: 'absolute',
            display: 'block',
            right: {
              sm: 0,
              md: isImageOnRight ? (imageAlign === 'end' ? '-60%' : '-30%') : 0,
            },
            left: { sm: 0, md: isImageOnRight ? 0 : '-30%' },
            height: isMobile ? '100%' : '100%',
            width: isMobile ? '100%' : 'auto',
            // rotate: idx === 1 ? '30deg' : idx === 2 ? '-150deg' : '0deg',
          }}
        />
      )}

      <Box
        sx={{
          background: isImageOnRight
            ? AppColors.aiGradient7
            : AppColors.aiGradient6,
          borderRadius: '16px 16px 0 0',
          px: 4,
        }}
      >
        <Typography
          sx={{
            color: 'white',
            // fontSize: 'clamp(1.125rem, 3vw, 2.125rem)',
            fontSize: typographyClamps.h5,

            // position: 'absolute',
            bottom: 5,
            left: idx === 0 || !isImageOnRight ? undefined : '50%',
            right: idx === 0 || isImageOnRight ? undefined : '50%',
          }}
        >
          {label}
        </Typography>
      </Box>

      <Box component='img' src={imagePath} width={'100%'} height='auto' />
    </Box>
  );

  const getFeatureListView = (list: Feature[]) => {
    return (
      <Box
        display='flex'
        flexDirection='column'
        justifyContent='center'
        alignItems='start'
      >
        {list.map((item, index) => (
          <Stack
            key={index}
            direction='row'
            alignItems='start'
            py={isMobile ? 0.5 : 1}
          >
            <Box
              component='img'
              src='/static/images/ic_ai_star.png'
              // height='32px'
              // width='32px'
              height={typographyClamps.fontSize24}
              width={typographyClamps.fontSize24}
              pt={1}
            />
            {/* )} */}
            <Box sx={{ pl: isMobile ? 1 : 2 }}>
              <Typography
                variant='body1'
                sx={{
                  // color: titleTextColor ?? AppColors.blackColor,
                  fontSize: isMobile
                    ? typographyClamps.h6
                    : typographyClamps.fontSize16,
                  fontWeight: 'bold',
                }}
              >
                {item.title}
              </Typography>
              <Typography
                variant='body2'
                sx={{
                  // color: titleTextColor ?? AppColors.blackColor,
                  fontSize: isMobile
                    ? typographyClamps.body1
                    : typographyClamps.fontSize14,
                  fontWeight: 500,
                }}
              >
                {item.description}
              </Typography>
            </Box>
          </Stack>
        ))}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        borderRadius: 8,
        background: isImageOnRight
          ? `url('/static/how_it_works_page/img_how_it_works_card_bg.png')`
          : AppColors.lightBlueTransparentColor,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        my: 4,
      }}
    >
      <Grid
        container
        gap={{ sx: 0, sm: 5, md: 1 }}
        direction='row'
        justifyContent='space-between'
        alignItems={'center'}
        py={3}
        px={{ xs: 2, sm: '5%', md: '8%', lg: '10%' }}
      >
        {isImageOnRight && <Grid size={gridSize}></Grid>}
        <Grid size={gridSize}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: isImageOnRight ? 'end' : 'start',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Typography
              sx={{ fontSize: typographyClamps.h4, fontWeight: 'bold' }}
            >
              Step
            </Typography>
            <Avatar
              sx={{
                background: AppColors.aiGradient5,
                p: 0.5,
              }}
            >
              <Typography
                sx={{
                  fontSize: typographyClamps.h4,
                  fontWeight: 'bold',
                }}
              >
                {idx + 1}
              </Typography>
            </Avatar>
          </Box>
        </Grid>
        {!isImageOnRight && <Grid size={gridSize}></Grid>}

        {/* {!isMobile && !isImageOnRight && ( */}
        {!(isForFeature && isMobile) && !isImageOnRight && (
          <Grid size={gridSize}>{getScreenshotView()}</Grid>
        )}

        <Grid size={gridSize}>
          <Box
            width={'100%'}
            display='flex'
            flexDirection='column'
            justifyContent='center'
            alignItems={{ xs: 'center', sm: 'center', md: 'start' }}
          >
            {title && (
              <Typography
                component='p'
                sx={{
                  // fontSize: 'clamp(1.25rem, 2vw, 2rem)',
                  fontSize: {
                    xs: typographyClamps.caption,
                    sm: typographyClamps.h6,
                    md: typographyClamps.h5,
                  },
                  fontWeight: 'bold',
                  color: titleTextColor ?? AppColors.whiteColor,
                  textAlign: isMobile ? 'center' : 'start',
                  width: '100%',
                  my: 1,
                  '& span': {
                    background: AppColors.textGradient,
                    WebkitBackgroundClip: 'text',
                    color: 'transparent',
                  },
                }}
              >
                {title}
              </Typography>
            )}

            {subtitle && (
              <Typography
                component='p'
                textAlign={{ xs: 'center', sm: 'center', md: 'start' }}
                fontWeight={400}
                py={1}
                color='#6B727A'
              >
                <HtmlRenderer htmlString={subtitle} />
              </Typography>
            )}

            {isForFeature && isMobile && (
              <Box py={2}>{getScreenshotView()}</Box>
            )}

            {features && getFeatureListView(features)}

            {footerText && (
              <Typography
                sx={{ py: 1, color: AppColors.redColor, fontStyle: 'italic' }}
              >
                {footerText}
              </Typography>
            )}

            {/* <GradientButton sx={{ mt: 2 }} onClick={() => {}}>
            {isForFeature ? 'Learn More' : 'Get Started'}
          </GradientButton> */}
          </Box>
        </Grid>

        {/* {!isMobile && isImageOnRight && ( */}
        {!(isForFeature && isMobile) && isImageOnRight && (
          <Grid size={gridSize}>{getScreenshotView()}</Grid>
        )}
      </Grid>
    </Box>
  );
};
